import { TFunction } from "i18next";
import { jsPDF } from "jspdf";
import { IConversationMessageModel, IConversationModel } from "../../Services/ConversationService";
import { formatDisplayDate } from "../../Util/dateUtil";
import {
    PDF_CONTENT_WIDTH,
    PDF_EMPTY_VALUE,
    PDF_FONT_BODY,
    PDF_FONT_SMALL,
    PDF_FONT_TITLE,
    PDF_LINE_HEIGHT,
    PDF_MARGIN_X,
    PDF_MARGIN_TOP,
    PDF_PAGE_WIDTH,
    ensurePageSpace,
    finalizePdfDocument,
    setTextColor,
    writeWrappedLines,
} from "../../Pages/Project/projectPdfExportShared";
import { resolveCustomerPdfLogo, resolveDashlyPdfLogo } from "../../Pages/Project/projectPdfLogo";
import { resolveProjectPdfTheme } from "../../Pages/Project/projectPdfTheme";

export const CONVERSATION_PDF_SKIP_CODES = ["ASKAITHNK", "ASKAIGNRN", "ASKAIFAIL"] as const;

export const htmlToConversationPlainText = (html: string | undefined): string => {
    if (!html) {
        return "";
    }
    if (typeof document === "undefined") {
        return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    }
    const container = document.createElement("div");
    container.innerHTML = html;
    const text = container.textContent ?? container.innerText ?? "";
    return text.replace(/\u00a0/g, " ").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
};

export const isConversationPdfExportable = (message: IConversationMessageModel): boolean => {
    if (!message || message.uuid === "SENDING") {
        return false;
    }
    const code = message.conversationMessageType?.code;
    if (!code || (CONVERSATION_PDF_SKIP_CODES as readonly string[]).includes(code)) {
        return false;
    }
    return !!htmlToConversationPlainText(message.message);
};

interface IExportConversationToPdfParams {
    conversation: IConversationModel;
    messages: IConversationMessageModel[];
    type: "ASKAI" | "MESSAGING" | undefined;
    t: TFunction;
}

const messageHeading = (
    message: IConversationMessageModel,
    type: IExportConversationToPdfParams["type"],
    t: TFunction,
): string => {
    const code = message.conversationMessageType?.code;
    const role = type === "ASKAI"
        ? (code === "ASKAIQSTN" ? t("conversation.export.question") : t("conversation.export.answer"))
        : (message.createdBy?.name ?? t("conversation.unknown-sender"));
    const timestamp = message.messageSentTs ? formatDisplayDate(message.messageSentTs) : "";
    return timestamp ? `${role} · ${timestamp}` : role;
};

export const exportConversationToPdf = async ({
    conversation,
    messages,
    type,
    t,
}: IExportConversationToPdfParams): Promise<void> => {
    const theme = resolveProjectPdfTheme();
    const title = conversation.title?.trim() || t("conversation.ask-ai");
    const exportable = messages.filter(isConversationPdfExportable);
    const [dashlyLogo, customerLogo] = await Promise.all([
        resolveDashlyPdfLogo(),
        resolveCustomerPdfLogo(conversation.customer?.uuid ?? undefined),
    ]);

    const doc = new jsPDF();
    let y = PDF_MARGIN_TOP;

    const logos = [customerLogo, dashlyLogo].filter(Boolean);
    if (logos.length > 0) {
        let cursorX = PDF_PAGE_WIDTH - PDF_MARGIN_X;
        logos.forEach((logo) => {
            if (!logo) {
                return;
            }
            cursorX -= logo.widthMm;
            doc.addImage(logo.dataUrl, logo.format, cursorX, y - 4, logo.widthMm, logo.heightMm);
            cursorX -= 4;
        });
    }

    doc.setFontSize(PDF_FONT_TITLE);
    doc.setFont("helvetica", "bold");
    setTextColor(doc, theme.primary);
    const titleWidth = logos.length > 0 ? PDF_CONTENT_WIDTH - 50 : PDF_CONTENT_WIDTH;
    y = writeWrappedLines(doc, doc.splitTextToSize(title, titleWidth), PDF_MARGIN_X, y + 4, 6.2, title, theme);

    doc.setFontSize(PDF_FONT_SMALL);
    doc.setFont("helvetica", "normal");
    setTextColor(doc, theme.muted);
    y = writeWrappedLines(
        doc,
        [t("conversation.export.exportedOn", { date: formatDisplayDate(new Date().toISOString()) })],
        PDF_MARGIN_X,
        y + 1,
        PDF_LINE_HEIGHT,
        title,
        theme,
    );
    y += 4;

    if (exportable.length === 0) {
        doc.setFontSize(PDF_FONT_BODY);
        setTextColor(doc, theme.muted);
        y = writeWrappedLines(doc, [t("conversation.export.empty")], PDF_MARGIN_X, y, PDF_LINE_HEIGHT, title, theme);
        finalizePdfDocument(doc, title, t("conversation.export.filenameSuffix"), theme);
        return;
    }

    for (const message of exportable) {
        const heading = messageHeading(message, type, t);
        const body = htmlToConversationPlainText(message.message) || PDF_EMPTY_VALUE;
        y = ensurePageSpace(doc, y, PDF_LINE_HEIGHT * 3, title, theme);
        doc.setFontSize(PDF_FONT_SMALL);
        doc.setFont("helvetica", "bold");
        setTextColor(doc, theme.primary);
        y = writeWrappedLines(
            doc,
            doc.splitTextToSize(heading, PDF_CONTENT_WIDTH),
            PDF_MARGIN_X,
            y,
            PDF_LINE_HEIGHT,
            title,
            theme,
        ) + 1;
        doc.setFontSize(PDF_FONT_BODY);
        doc.setFont("helvetica", "normal");
        setTextColor(doc, theme.text);
        y = writeWrappedLines(
            doc,
            doc.splitTextToSize(body, PDF_CONTENT_WIDTH),
            PDF_MARGIN_X,
            y,
            PDF_LINE_HEIGHT,
            title,
            theme,
        ) + 4;
    }

    finalizePdfDocument(doc, title, t("conversation.export.filenameSuffix"), theme);
};
